import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
    LANG_STORAGE_KEY,
    LANGUAGE_FILE_URLS_KEY,
    LanguagesApiData,
    PortalLanguage,
    SESSION_LANG_KEY,
    USER_PREF_LANG_KEY,
    readSavedUserLanguage,
} from '../core/Models/language.model';
import { ConfigurationService } from './configuration.service';

const LANG_SEGMENT = /^[a-z0-9-]{2,16}$/;
const LS_ETAG_PREFIX = 'tg.portal.i18n.etag.';
const LS_JSON_PREFIX = 'tg.portal.i18n.json.';

const languageFileUrls = new Map<string, string>();

function registerLanguageFileUrls(languages: PortalLanguage[]): void {
    for (const lang of languages) {
        const code = lang.languageCode?.trim().toLowerCase();
        const url = lang.fileURL?.trim();
        if (code && url) {
            languageFileUrls.set(code, url);
        }
    }
    try {
        sessionStorage.setItem(LANGUAGE_FILE_URLS_KEY, JSON.stringify(Object.fromEntries(languageFileUrls)));
    } catch {
        /* ignore */
    }
}

function hydrateLanguageFileUrls(): void {
    const raw = sessionStorage.getItem(LANGUAGE_FILE_URLS_KEY);
    if (!raw) {
        languageFileUrls.clear();
        return;
    }
    if (languageFileUrls.size > 0) {
        return;
    }
    try {
        const parsed = JSON.parse(raw) as Record<string, string>;
        Object.entries(parsed).forEach(([code, url]) => {
            if (code && url) {
                languageFileUrls.set(code.toLowerCase(), url);
            }
        });
    } catch {
        languageFileUrls.clear();
    }
}

function getLanguageFileUrl(languageCode: string): string | null {
    hydrateLanguageFileUrls();
    return languageFileUrls.get(languageCode.trim().toLowerCase()) ?? null;
}

function clearLanguageFileUrls(): void {
    languageFileUrls.clear();
    sessionStorage.removeItem(LANGUAGE_FILE_URLS_KEY);
}

function resolveBrowserLanguage(availableLanguages: PortalLanguage[]): string {
    const available = availableLanguages
        .map((l) => l.languageCode?.toLowerCase())
        .filter((code): code is string => !!code);

    const browserCodes = (
        navigator.languages?.length ? navigator.languages : [navigator.language || 'en']
    ).map((l) => l.split('-')[0].toLowerCase());

    for (const code of browserCodes) {
        if (available.includes(code)) {
            return code;
        }
    }

    if (available.includes('en')) {
        return 'en';
    }

    return available[0] ?? 'en';
}

function resolveActiveLanguage(
    savedUserLanguage: string | null | undefined,
    availableLanguages: PortalLanguage[]
): string {
    if (savedUserLanguage) {
        const normalized = savedUserLanguage.toLowerCase();
        const match = availableLanguages.find(
            (l) => l.languageCode?.toLowerCase() === normalized
        );
        if (match?.languageCode) {
            return match.languageCode;
        }
    }

    return resolveBrowserLanguage(availableLanguages);
}

function apiOriginFromBaseUrl(baseUrl: string): string {
    const trimmed = baseUrl.trim().replace(/\/+$/, '');
    return trimmed.replace(/\/api\/?$/i, '');
}

function portalI18nUrl(lang: string): string {
    const origin = apiOriginFromBaseUrl(environment.baseUrl).replace(/\/+$/, '');
    return `${origin}/portal/i18n/${lang}.json`;
}
function localUrl(lang: string): string
{
    return `/assets/i18n/${lang}.json`;
}

class PortalTranslateLoader implements TranslateLoader {
    private readonly defaultLang = 'en';
    private readonly memory = new Map<string, { etag: string; body: Record<string, any> }>();

    constructor(private readonly http: HttpClient) {}

    private resolveUrl(lang: string): string {
        if(environment.production)
        {
            return portalI18nUrl(lang);
        }else{
            return localUrl(lang);
        }
    }

    getTranslation(lang: string): Observable<Record<string, any>> {
        const normalized = (lang || '').trim().toLowerCase();
        if (!LANG_SEGMENT.test(normalized)) {
            return normalized === this.defaultLang ? of({}) : this.getTranslation(this.defaultLang);
        }
        return this.loadWithEtag(normalized).pipe(
            catchError((err: HttpErrorResponse) => this.handleHttpError(err, normalized))
        );
    }

    private getCached(lang: string): { etag: string; body: Record<string, any> } | undefined {
        const mem = this.memory.get(lang);
        if (mem) {
            return mem;
        }
        try {
            const etag = localStorage.getItem(LS_ETAG_PREFIX + lang);
            const raw = localStorage.getItem(LS_JSON_PREFIX + lang);
            if (etag != null && raw != null) {
                const body = JSON.parse(raw) as Record<string, any>;
                const entry = { etag, body };
                this.memory.set(lang, entry);
                return entry;
            }
        } catch {
            /* ignore corrupt cache */
        }
        return undefined;
    }

    private clearDisk(lang: string): void {
        try {
            localStorage.removeItem(LS_ETAG_PREFIX + lang);
            localStorage.removeItem(LS_JSON_PREFIX + lang);
        } catch {
            /* ignore */
        }
    }

    private persist(lang: string, etag: string, body: Record<string, any>): void {
        const entry = { etag, body };
        this.memory.set(lang, entry);
        if (!etag) {
            return;
        }
        try {
            this.clearDisk(lang);
            localStorage.setItem(LS_ETAG_PREFIX + lang, etag);
            localStorage.setItem(LS_JSON_PREFIX + lang, JSON.stringify(body));
        } catch {
            /* quota or private mode */
        }
    }

    private loadWithEtag(lang: string): Observable<Record<string, any>> {
        return this.http
            .get<unknown>(this.resolveUrl(lang), {
                observe: 'response',
                responseType: 'json',
            })
            .pipe(
                map((resp: HttpResponse<unknown>) => {
                    if (resp.status === 304) {
                        const fromCache = this.getCached(lang);
                        return fromCache?.body ?? {};
                    }

                    const body = (resp.body ?? {}) as Record<string, any>;
                    const etag = resp.headers.get('ETag');

                    if (etag) {
                        this.persist(lang, etag, body);
                    }

                    return body;
                })
            );
    }

    private handleHttpError(err: HttpErrorResponse, lang: string): Observable<Record<string, any>> {
        if (err.status === 404 || err.status === 400 || err.status === 415) {
            if (lang !== this.defaultLang) {
                return this.getTranslation(this.defaultLang);
            }
            return of({});
        }

        const cached = this.getCached(lang);
        if (cached) {
            return of(cached.body);
        }

        if (lang !== this.defaultLang) {
            return this.getTranslation(this.defaultLang).pipe(catchError(() => of({})));
        }

        return of({});
    }
}

export function portalTranslateLoaderFactory(http: HttpClient): TranslateLoader {
    return new PortalTranslateLoader(http);
}

/** Manages portal language list, browser matching, and user preference persistence. */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    private languages: PortalLanguage[] = [];
    private activeLanguageCode = localStorage.getItem(LANG_STORAGE_KEY) || 'en';
    private loginLanguageHint: string | null = null;
    private loginSyncHandled = false;
    private syncQueue: Promise<void> = Promise.resolve();

    private readonly configurationService = inject(ConfigurationService);
    private readonly translate = inject(TranslateService);

    private readonly languagesSubject = new BehaviorSubject<PortalLanguage[]>([]);
    readonly languages$ = this.languagesSubject.asObservable();

    private readonly activeLanguageSubject = new BehaviorSubject<string>(
        localStorage.getItem(LANG_STORAGE_KEY) || 'en'
    );
    readonly activeLanguage$ = this.activeLanguageSubject.asObservable();

    applyAfterLogin(loginUserLanguage: string | null | undefined): void {
        this.loginSyncHandled = false;
        this.loginLanguageHint = loginUserLanguage ?? null;
        if (this.loginLanguageHint) {
            this.persistUserPreference(this.loginLanguageHint);
        }
        this.enqueueSync(this.loginLanguageHint, 'applyAfterLogin');
    }

    applyOnAppLoad(): void {
        if (!localStorage.getItem('token')) {
            return;
        }
        if (this.loginLanguageHint != null || this.loginSyncHandled) {
            return;
        }
        this.enqueueSync(null, 'applyOnAppLoad');
    }

    refreshLanguages(): Promise<void> {
        return this.fetchLanguagesList();
    }

    async saveUserLanguageSelection(languageCode: string): Promise<void> {
        const response = await firstValueFrom(
            this.configurationService.saveUserLanguage(languageCode)
        );
        const parsed = this.parseLanguagesResponse(response);
        if (parsed.languages.length) {
            this.setLanguages(parsed.languages);
        } else {
            await this.fetchLanguagesList();
        }
        await this.applyLanguage(languageCode);
        this.persistUserPreference(languageCode);
    }

    getLanguages(): PortalLanguage[] {
        return this.languages;
    }

    getActiveLanguageCode(): string {
        return this.activeLanguageCode;
    }

    clearSessionLanguage(): void {
        sessionStorage.removeItem(SESSION_LANG_KEY);
        clearLanguageFileUrls();
    }

    private enqueueSync(loginUserLanguage: string | null, source: string): void {
        this.syncQueue = this.syncQueue
            .then(() => this.syncLanguage(loginUserLanguage, source))
            .catch(() => undefined)
            .finally(() => {
                if (source === 'applyAfterLogin') {
                    this.loginLanguageHint = null;
                    this.loginSyncHandled = true;
                }
            });
    }

    private async fetchLanguagesList(): Promise<void> {
        try {
            const parsed = await this.loadLanguagesFromApi();
            if (parsed.languages.length) {
                this.setLanguages(parsed.languages);
            }
        } catch {
            /* ignore */
        }
    }

    private async loadLanguagesFromApi(): Promise<LanguagesApiData> {
        let userLanguage: string | null = null;
        let languages: PortalLanguage[] = [];

        try {
            const response = await firstValueFrom(this.configurationService.getLanguagesList());
            const parsed = this.parseLanguagesResponse(response);
            languages = parsed.languages;
            userLanguage = parsed.userLanguage;
        } catch {
            /* ignore */
        }

        return { languages, userLanguage };
    }

    private resolveDbLanguage(
        apiLanguage: string | null,
        loginUserLanguage: string | null,
        languages: PortalLanguage[]
    ): string | null {
        const candidates = [
            apiLanguage,
            loginUserLanguage,
            localStorage.getItem(USER_PREF_LANG_KEY),
            localStorage.getItem(LANG_STORAGE_KEY),
        ];
        for (const value of candidates) {
            if (value == null || !String(value).trim()) {
                continue;
            }
            const code = String(value).trim();
            if (!languages.length) {
                return code;
            }
            const match = languages.find(
                (l) => l.languageCode?.toLowerCase() === code.toLowerCase()
            );
            if (match?.languageCode) {
                return match.languageCode;
            }
        }
        return null;
    }

    private persistUserPreference(languageCode: string): void {
        const normalized = languageCode.trim().toLowerCase();
        localStorage.setItem(USER_PREF_LANG_KEY, normalized);
    }

    private async syncLanguage(loginUserLanguage: string | null, _source: string): Promise<void> {
        try {
            const parsed = await this.loadLanguagesFromApi();

            if (parsed.languages.length) {
                this.setLanguages(parsed.languages);
            }

            const dbLanguage = this.resolveDbLanguage(
                parsed.userLanguage,
                loginUserLanguage,
                this.languages
            );
            let activeLang: string;

            if (dbLanguage) {
                activeLang = resolveActiveLanguage(dbLanguage, this.languages);
                this.persistUserPreference(activeLang);
            } else {
                activeLang = resolveBrowserLanguage(this.languages);
                sessionStorage.setItem(SESSION_LANG_KEY, activeLang);
            }

            await this.applyLanguage(activeLang);
        } catch {
            /* ignore */
        }
    }

    private parseLanguagesResponse(response: unknown): LanguagesApiData {
        const res = response as {
            data?: unknown;
            userLanguage?: string | null;
            UserLanguage?: string | null;
            LanguageCode?: string | null;
        };
        const data = res?.data ?? response;

        if (data && typeof data === 'object' && !Array.isArray(data)) {
            const payload = data as {
                languages?: PortalLanguage[];
                userLanguage?: string | null;
                UserLanguage?: string | null;
                LanguageCode?: string | null;
            };
            if (Array.isArray(payload.languages)) {
                return {
                    languages: this.normalizeLanguages(payload.languages),
                    userLanguage: this.readSavedLanguage(res, payload),
                };
            }
            return { languages: [], userLanguage: this.readSavedLanguage(res, payload) };
        }

        if (Array.isArray(data)) {
            return {
                languages: this.normalizeLanguages(data),
                userLanguage: this.readSavedLanguage(res),
            };
        }

        return { languages: [], userLanguage: this.readSavedLanguage(res) };
    }

    private readSavedLanguage(
        res: Record<string, unknown>,
        payload?: Record<string, unknown>
    ): string | null {
        return readSavedUserLanguage(payload) ?? readSavedUserLanguage(res);
    }

    private normalizeLanguages(languages: PortalLanguage[]): PortalLanguage[] {
        return languages
            .filter((l) => !!l?.languageCode && !!l?.languageName)
            .map((l) => ({
                id: l.id,
                languageCode: l.languageCode,
                languageName: l.languageName,
                countryImage: l.countryImage ?? '',
                fileURL: l.fileURL,
            }));
    }

    private setLanguages(languages: PortalLanguage[]): void {
        if (!languages.length) {
            return;
        }
        this.languages = languages;
        registerLanguageFileUrls(languages);
        this.languagesSubject.next(languages);
    }

    private async applyLanguage(languageCode: string): Promise<void> {
        const normalized = (languageCode || 'en').trim().toLowerCase();
        this.activeLanguageCode = normalized;
        this.activeLanguageSubject.next(normalized);
        localStorage.setItem(LANG_STORAGE_KEY, normalized);
        sessionStorage.setItem(SESSION_LANG_KEY, normalized);
        this.translate.setDefaultLang('en');

        this.clearTranslateCache(normalized);
        await firstValueFrom(this.translate.use(normalized)).catch(() => undefined);
    }

    private clearTranslateCache(languageCode: string): void {
        const store = this.translate as { translations?: Record<string, unknown> };
        if (store.translations?.[languageCode]) {
            delete store.translations[languageCode];
        }
    }
}
